import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { TokenVerifyDetail, TokenVerifyResult } from '@volley/crucible-ipc-types/ipc-channels';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { INCOMPLETE_TOKEN_VERIFY_MESSAGE, isCompleteTokenVerifyResult } from '../token-verify';
import { CrucibleErrorState } from './CrucibleErrorState';
import { PrereqStatusList, hasMissingPrereqs } from './PrereqStatusList';

/**
 * First-run onboarding overlay.
 *
 * Tier-2 rework (board-member-audit §1, top-10 fix #10):
 *
 * Pre-Tier-2 the overlay demanded GitHub PAT, Anthropic key, and Fal.ai key
 * up front, before the user had seen a single game — three different secrets
 * at first launch. Most non-technical users bounced. Of the three, only the
 * Anthropic key is needed to talk to the in-app AI agent and start a game;
 * GitHub is a publish-time concern, Fal.ai a per-feature concern (artwork).
 *
 * The Tier-2 flow asked for the Anthropic key only. The other two had
 * one-line "you can add this later in Settings" affordances; their full
 * prompts moved into the publish flow and the art-generation action
 * respectively.
 *
 * **Issue #381 reinstates the Fal.ai prompt** with two important guard-rails
 * that distinguish it from the pre-Tier-2 friction:
 *
 *   1. **Optional, with an explicit decline path.** Unlike the Anthropic key
 *      the user can complete onboarding without one. Skipping requires going
 *      through a confirmation dialog — the brief calls this "explicit
 *      friction" so users notice the trade-off (placeholder SVGs instead of
 *      real generated imagery) before they bounce past it.
 *   2. **The decline is persisted.** The next launch doesn't re-nag. Users
 *      can re-trigger the prompt later via Settings → Connections (un-tick
 *      "use placeholders") or revoke a previously-provided key, mirroring
 *      the GitHub-token UX.
 *
 * The overlay also surfaces structured `tokens:verify` failures (network vs
 * key-invalid vs rate-limited) so a board member offline doesn't see the
 * same red cross as one who pasted a typo (failure-mode plan §2.1).
 */
type Step = 'welcome' | 'anthropic' | 'fal' | 'done';

export function OnboardingOverlay({ onComplete }: { onComplete: () => void }): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('welcome');
  const [anthropicKey, setAnthropicKey] = useState('');
  // #381: Fal.ai input state. Separate from Anthropic so a user backing
  // out of the confirmation dialog returns to a typed value (or a clean
  // empty box) rather than picking up the Anthropic key by accident.
  const [falKey, setFalKey] = useState('');
  const [falSaving, setFalSaving] = useState(false);
  const [falError, setFalError] = useState<SerialisedError | null>(null);
  // Whether the "Skip — generate game images later" CTA has been clicked.
  // True puts the panel in confirmation-dialog mode; "Add key now" returns
  // to the input state, "Skip anyway" records the decline and advances.
  const [confirmingSkipFal, setConfirmingSkipFal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // IPC-level errors (save/verify itself crashed). Tier 3 structured
  // form so the user sees recovery + a Try-again. The verifyDetail path
  // below stays as plain copy because it carries its own actionable
  // text from the helper ("Couldn't reach Anthropic" vs "Key rejected").
  const [error, setError] = useState<SerialisedError | null>(null);
  const [verifyDetail, setVerifyDetail] = useState<TokenVerifyDetail | null>(null);
  // Result of the eager prereq check that runs on mount. We don't
  // gate continuation on prereqs (a user can paste their Anthropic
  // key and explore the chat without git/pnpm), but we surface
  // missing tools up front so they know what they'll need before
  // hitting Create New Game.
  const [prereqStatus, setPrereqStatus] = useState<TokenVerifyResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    void window.crucible
      .invoke('tokens:verify')
      .then((result) => {
        if (!cancelled && isCompleteTokenVerifyResult(result)) {
          setPrereqStatus(result);
        }
      })
      .catch(() => {
        // Silently ignore — `tokens:verify` failing here is an
        // app-startup concern, not an onboarding-blocker. The
        // overlay's existing error path covers the post-paste
        // verify call and that's the user-visible recovery surface.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Save and verify in one click. We save first because tokens:verify reads
   * from the same secure store the agent will, and we want the renderer's
   * tokens:get cache to invalidate exactly once. If verify reports
   * `network`, we DO NOT clear the key — the user shouldn't lose their
   * paste because their Wi-Fi blipped.
   */
  const handleSaveAndVerify = async (): Promise<void> => {
    const value = anthropicKey.trim();
    if (!value) return;
    setSaving(true);
    setError(null);
    setVerifyDetail(null);
    try {
      await window.crucible.invoke('tokens:set', { key: 'anthropic', value });
      void queryClient.invalidateQueries({ queryKey: ['tokens:get'] });
    } catch (err) {
      setError(unflattenIpcError(err));
      setSaving(false);
      return;
    }
    setSaving(false);

    // Now verify. We surface the structured TokenVerifyDetail so users see
    // "we couldn't reach Anthropic" vs "your key was rejected".
    setVerifying(true);
    try {
      const result = await window.crucible.invoke('tokens:verify');
      if (!isCompleteTokenVerifyResult(result)) {
        throw new Error(INCOMPLETE_TOKEN_VERIFY_MESSAGE);
      }
      const detail = result.anthropic;
      setVerifyDetail(detail);
      if (detail.status === 'valid') {
        // #381: route through the Fal.ai prompt instead of jumping
        // straight to 'done'. The Fal step has its own skip path
        // that calls `onComplete` so we don't strand the user.
        setStep('fal');
      }
      // If detail.status is 'invalid', we leave the user on this step with
      // the actionable copy from the helper. They can edit and try again.
    } catch (err) {
      // tokens:verify threw or returned an incomplete runtime payload —
      // surface via the Tier 3 structured error.
      setError(unflattenIpcError(err));
    } finally {
      setVerifying(false);
    }
  };

  /**
   * Issue #381: persist a Fal.ai key. We deliberately do NOT round-trip a
   * Fal API call here — the brief calls this out explicitly:
   *
   *   "Validating the Fal.ai key by making an API call during onboarding
   *    (slow, costs money) — reserve for an explicit 'Test key' button
   *    in Settings"
   *
   * Invalid keys surface at first hero-image generation. Empty input is
   * a no-op (the button is disabled), and a malformed key is the user's
   * problem to spot at use-time. This keeps onboarding fast.
   */
  const handleSaveFalKey = async (): Promise<void> => {
    const value = falKey.trim();
    if (!value) return;
    setFalSaving(true);
    setFalError(null);
    try {
      await window.crucible.invoke('tokens:set', { key: 'fal', value });
      // If a previous run left `falKeyDeclined` set (e.g. user declined,
      // changed their mind, came back via Settings), clear it now that a
      // real key is in place.
      await window.crucible.invoke('app:set-fal-key-declined', { declined: false });
      void queryClient.invalidateQueries({ queryKey: ['tokens:get'] });
      void queryClient.invalidateQueries({ queryKey: ['app:fal-key-declined'] });
      setFalKey('');
      setStep('done');
    } catch (err) {
      setFalError(unflattenIpcError(err));
    } finally {
      setFalSaving(false);
    }
  };

  /**
   * #381: explicit decline path. Records `falKeyDeclined: true` so the
   * onboarding doesn't re-nag on next launch. The Settings page still
   * shows the row so the user can change their mind any time.
   */
  const handleDeclineFalKey = async (): Promise<void> => {
    setFalSaving(true);
    setFalError(null);
    try {
      await window.crucible.invoke('app:set-fal-key-declined', { declined: true });
      void queryClient.invalidateQueries({ queryKey: ['app:fal-key-declined'] });
      setStep('done');
    } catch (err) {
      setFalError(unflattenIpcError(err));
    } finally {
      setFalSaving(false);
    }
  };

  const handleSkipForNow = (): void => {
    // Mark onboarding done even though no key was provided. The rest of the
    // app already handles the missing-key path: Settings shows banners, the
    // Workspace shows an inline CTA when no key is set. The user can come
    // back any time.
    onComplete();
  };

  const goToSettings = (): void => {
    onComplete();
    void navigate('/settings?tab=connections');
  };

  return (
    // `app-region-no-drag` — the OnboardingOverlay mounts on the
    // Dashboard, which is now `app-region-drag` (drag-fix PR). The
    // overlay's `fixed inset-0` covers the page including the header
    // strip; without the opt-out, a Get-Started / Skip click landing
    // near the top of the window can be interpreted as window-drag
    // by the macOS compositor. Mirrors the PreviewOverlay /
    // UpdateTemplateModal / CreateGameDialog pattern.
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-crucible-surface p-8 shadow-2xl">
        {/* Step: Welcome */}
        {step === 'welcome' && (
          <div className="flex flex-col items-center gap-6 text-center">
            <h1 className="text-2xl font-bold">
              <span className="glint-brand">Welcome to Foundry</span>
            </h1>
            <p className="text-white/60">
              Build TV games with AI. Describe what you want, and Foundry builds it.
            </p>
            <p className="text-sm text-white/40">We just need a couple of things to get started.</p>

            {/* Surface any missing system prereqs before the user
                commits to pasting a key. We don't block continuation
                — the chat panel works fine without git/pnpm — but
                they'll need these before Create New Game succeeds, so
                better to flag now than fail mid-create later. */}
            {prereqStatus && hasMissingPrereqs(prereqStatus) && (
              <div className="w-full text-left">
                <p className="mb-2 text-xs uppercase tracking-wide text-amber-300/80">
                  System tools needed for Create New Game
                </p>
                <PrereqStatusList verify={prereqStatus} mode="missing-only" />
                <p className="mt-2 text-xs text-white/30">
                  You can finish setup and add these later — you just won&apos;t be able to scaffold
                  a new game until they&apos;re installed.
                </p>
              </div>
            )}

            <button
              onClick={() => setStep('anthropic')}
              className="rounded-lg bg-crucible-accent px-6 py-3 font-medium text-white hover:opacity-90"
            >
              Get Started
            </button>
            <p className="text-xs text-white/30">
              You can skip onboarding, but This Mac stays disabled until GitHub, Anthropic, Git,
              Node.js, and pnpm are ready.
            </p>
          </div>
        )}

        {/* Step: Anthropic key */}
        {step === 'anthropic' && (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold">Add your AI key</h2>
              <p className="mt-1 text-sm text-white/50">
                Foundry uses Anthropic Claude to talk to you and build your game. Without this key,
                Local AI planning, builds, and chat are disabled.
              </p>
            </div>
            <ol className="ml-4 list-decimal space-y-1 text-sm text-white/40">
              <li>
                Get the key from your team lead or{' '}
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-crucible-accent hover:underline"
                >
                  console.anthropic.com
                </a>
              </li>
              <li>
                Paste it below — it starts with{' '}
                <code className="rounded bg-white/10 px-1">sk-ant-</code>
              </li>
            </ol>
            <input
              type="password"
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              placeholder="sk-ant-..."
              autoFocus
              className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm placeholder-white/30 focus:border-crucible-accent focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && anthropicKey.trim() && !saving && !verifying) {
                  void handleSaveAndVerify();
                }
              }}
            />

            {/* Structured verify failure surface — distinguishes network/etc */}
            {verifyDetail?.status === 'invalid' && (
              <div
                role="alert"
                data-testid="onboarding-verify-error"
                data-verify-reason={verifyDetail.reason}
                className={`rounded-lg border px-3 py-2 text-sm ${
                  verifyDetail.reason === 'network'
                    ? 'border-yellow-500/30 bg-yellow-500/5 text-yellow-300'
                    : 'border-red-500/30 bg-red-500/5 text-red-300'
                }`}
              >
                {verifyDetail.error ?? 'Verification failed.'}
              </div>
            )}

            {/* IPC-level errors (save/verify itself crashed) — Tier 3
                structured form lets the user retry the save+verify chain
                without re-typing their key. */}
            {error && (
              <CrucibleErrorState
                error={error}
                compact
                onRetry={() => void handleSaveAndVerify()}
              />
            )}

            <div className="flex flex-col gap-2">
              <button
                onClick={() => void handleSaveAndVerify()}
                disabled={!anthropicKey.trim() || saving || verifying}
                className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                {saving ? 'Saving...' : verifying ? 'Verifying...' : 'Save & Continue'}
              </button>
              <div className="flex justify-between text-xs">
                <button onClick={handleSkipForNow} className="text-white/30 hover:text-white/50">
                  Skip for now (add later in Settings)
                </button>
                <button onClick={goToSettings} className="text-white/30 hover:text-white/50">
                  Open Settings instead
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step: Fal.ai key (issue #381 — optional, with explicit decline) */}
        {step === 'fal' && !confirmingSkipFal && (
          <FalKeyEntryStep
            falKey={falKey}
            onChange={setFalKey}
            saving={falSaving}
            error={falError}
            onSave={() => void handleSaveFalKey()}
            onSkip={() => setConfirmingSkipFal(true)}
          />
        )}

        {/* Step: Fal.ai skip-confirmation dialog */}
        {step === 'fal' && confirmingSkipFal && (
          <FalKeySkipConfirm
            saving={falSaving}
            error={falError}
            onAddKeyNow={() => setConfirmingSkipFal(false)}
            onSkipAnyway={() => void handleDeclineFalKey()}
          />
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold">Onboarding complete</h2>
            <p className="text-sm text-white/50">
              Click <strong className="text-white/80">+ New Game</strong> on the dashboard to start.
              Building on This Mac becomes available after GitHub, Anthropic, and the Local
              developer tools are verified. Fal.ai remains optional and uses SVG fallback artwork
              when absent. Manage all keys in Settings &rarr; Connections.
            </p>
            <button
              onClick={onComplete}
              className="rounded-lg bg-crucible-accent px-6 py-3 font-medium text-white hover:opacity-90"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Step indicator — four steps now (welcome, anthropic, fal, done).
            The Fal.ai step is reinstated by issue #381 with an explicit
            decline path; the dot count reflects that. */}
        <div className="mt-6 flex justify-center gap-2" aria-label="Onboarding progress">
          {(['welcome', 'anthropic', 'fal', 'done'] as const).map((s) => (
            <div
              key={s}
              className={`h-1.5 w-1.5 rounded-full ${
                s === step ? 'bg-crucible-accent' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fal.ai onboarding step (issue #381) — props-driven so SSR component tests
// can render each branch (entry vs confirmation) without driving the parent
// state machine. Both sub-components are exported for that reason.
// ---------------------------------------------------------------------------

/**
 * The "paste a Fal.ai key" entry panel. Mirrors the visual shape of the
 * Anthropic step but the copy emphasises that this step is optional —
 * skipping is a first-class CTA that routes through {@link FalKeySkipConfirm}.
 */
export function FalKeyEntryStep({
  falKey,
  onChange,
  saving,
  error,
  onSave,
  onSkip,
}: {
  falKey: string;
  onChange: (value: string) => void;
  saving: boolean;
  error: SerialisedError | null;
  onSave: () => void;
  onSkip: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid="onboarding-fal-step">
      <div>
        <h2 className="text-lg font-semibold">Add your Fal.ai key</h2>
        <p className="mt-1 text-sm text-white/50">
          Fal.ai generates real artwork for your game tiles and the art-director&apos;s style
          picker. Without it, generated images fall back to SVG placeholders.
        </p>
        <p className="mt-2 text-xs text-white/40">
          This is optional — you can skip and add it later in Settings &rarr; Connections, or
          decline entirely if your games don&apos;t need runtime image generation.
        </p>
      </div>
      <ol className="ml-4 list-decimal space-y-1 text-sm text-white/40">
        <li>
          Find or create a key at{' '}
          <a
            href="https://fal.ai/dashboard/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-crucible-accent hover:underline"
          >
            fal.ai/dashboard/keys
          </a>
        </li>
        <li>Paste it below — Fal keys are usually a long opaque string.</li>
      </ol>
      <input
        type="password"
        value={falKey}
        onChange={(e) => onChange(e.target.value)}
        placeholder="fal-ai key (find one at https://fal.ai/dashboard/keys)"
        autoFocus
        data-testid="onboarding-fal-input"
        className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm placeholder-white/30 focus:border-crucible-accent focus:outline-none"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && falKey.trim() && !saving) {
            onSave();
          }
        }}
      />

      {error && <CrucibleErrorState error={error} compact onRetry={onSave} />}

      <div className="flex flex-col gap-2">
        <button
          onClick={onSave}
          disabled={!falKey.trim() || saving}
          className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Save key & continue'}
        </button>
        <button
          onClick={onSkip}
          disabled={saving}
          data-testid="onboarding-fal-skip"
          className="rounded-lg border border-white/10 bg-transparent px-4 py-2 text-sm text-white/60 hover:border-white/20 hover:text-white/80 disabled:opacity-40"
        >
          Skip — generate game images later
        </button>
      </div>
    </div>
  );
}

/**
 * Confirmation dialog the user lands on after clicking "Skip — generate
 * game images later". The brief explicitly required this two-button
 * confirmation so the user must consciously trade real artwork for SVG
 * placeholders (rather than skipping by accident).
 *
 * - "Add key now"  → returns to {@link FalKeyEntryStep}
 * - "Skip anyway"  → records `falKeyDeclined: true` and completes onboarding
 */
export function FalKeySkipConfirm({
  saving,
  error,
  onAddKeyNow,
  onSkipAnyway,
}: {
  saving: boolean;
  error: SerialisedError | null;
  onAddKeyNow: () => void;
  onSkipAnyway: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid="onboarding-fal-confirm-skip">
      <div>
        <h2 className="text-lg font-semibold">Skip Fal.ai key?</h2>
        <p className="mt-2 text-sm text-white/60">
          Without a Fal.ai key, generated images in your games will be SVG placeholders. The
          art-director&apos;s &ldquo;pick a visual style&rdquo; prompt will show low-quality samples
          instead of real generated imagery.
        </p>
        <p className="mt-2 text-sm text-white/60">
          You can add a key later in Settings &rarr; Connections.
        </p>
      </div>

      {error && <CrucibleErrorState error={error} compact onRetry={onSkipAnyway} />}

      <div className="flex flex-col gap-2">
        <button
          onClick={onAddKeyNow}
          disabled={saving}
          className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          Add key now
        </button>
        <button
          onClick={onSkipAnyway}
          disabled={saving}
          data-testid="onboarding-fal-skip-anyway"
          className="rounded-lg border border-white/10 bg-transparent px-4 py-2 text-sm text-white/60 hover:border-white/20 hover:text-white/80 disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Skip anyway'}
        </button>
      </div>
    </div>
  );
}
